import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
  jsonLd,
}: SEOHeadProps) {
  const fullTitle = title.includes('51CG') ? title : `${title} - 51CG吃瓜特色`;
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : '');
  const imageUrl = ogImage || `${siteUrl}/og-default.jpg`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('canonical', canonicalUrl, 'rel');
    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', imageUrl, 'property');
    setMeta('og:site_name', '51CG吃瓜特色', 'property');
    setMeta('og:locale', 'zh_CN', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', imageUrl);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime, 'property');
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime, 'property');
    if (articleAuthor) setMeta('article:author', articleAuthor, 'property');
    if (articleSection) setMeta('article:section', articleSection, 'property');
    articleTags?.forEach((tag) => setMeta('article:tag', tag, 'property'));

    // JSON-LD
    const existing = document.querySelectorAll('script[data-seo="jsonld"]');
    existing.forEach((e) => e.remove());

    const scripts: Record<string, unknown>[] = [];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) {
        scripts.push(...jsonLd);
      } else {
        scripts.push(jsonLd);
      }
    }

    scripts.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'jsonld');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [fullTitle, description, keywords, canonicalUrl, imageUrl, ogType, articlePublishedTime, articleModifiedTime, articleAuthor, articleSection, articleTags, jsonLd]);

  return null;
}